import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, Database, Cloud, Zap, Clock, Shield,
  TrendingUp, Code, CheckCircle2, Globe, Server, GitBranch
} from 'lucide-react';

const DataMigrationServices = () => {
  const services = [
    {
      icon: Cloud,
      title: 'Cloud Data Migration',
      description: 'Migrate data workloads to AWS, Azure, or GCP with zero-downtime strategies and full validation',
      features: ['AWS RDS / Redshift / S3', 'Azure SQL / Synapse / Blob', 'GCP BigQuery / Cloud SQL', 'Automated validation & rollback']
    },
    {
      icon: Database,
      title: 'Database Migration',
      description: 'Migrate from legacy databases (Oracle, SQL Server, MySQL) to modern cloud-native solutions',
      features: ['Oracle → PostgreSQL', 'SQL Server → Azure SQL', 'MySQL → Aurora / Cloud SQL', 'Schema translation & testing']
    },
    {
      icon: Server,
      title: 'Data Warehouse Migration',
      description: 'Move your analytics workloads from on-premise warehouses to Snowflake, BigQuery, or Redshift',
      features: ['Teradata / Netezza migration', 'ETL pipeline replatforming', 'dbt model migration', 'Performance benchmarking']
    },
    {
      icon: GitBranch,
      title: 'Legacy System Modernisation',
      description: 'Extract data from legacy systems, transform for modern architectures, and build new integration layers',
      features: ['ERP data extraction', 'Legacy mainframe integration', 'API layer development', 'Gradual cut-over strategy']
    },
    {
      icon: Zap,
      title: 'Real-Time Data Migration',
      description: 'Live migrations with CDC (Change Data Capture) so production never goes down during the move',
      features: ['AWS DMS / Debezium', 'Kafka-based replication', 'Dual-write strategies', 'Lag monitoring & alerting']
    },
    {
      icon: Shield,
      title: 'Compliance-First Migration',
      description: 'Migrate sensitive data (HIPAA, GDPR, PCI-DSS) with encryption, audit trails, and access controls intact',
      features: ['Data classification', 'Encryption in transit & at rest', 'Audit log migration', 'Compliance documentation']
    }
  ];

  const caseStudies = [
    {
      industry: 'FMCG',
      challenge: 'Legacy on-premise data warehouse holding 10TB of sales data — slow queries, high maintenance, no scalability',
      solution: 'Migrated to Snowflake with dbt-powered transformation layer. Zero downtime using dual-write during cut-over.',
      result: 'Query time cut by 90%. Infrastructure costs down 60%. Analytics team now self-serve.',
      timeline: '8 weeks',
      link: '/case-studies/fmcg-migration'
    },
    {
      industry: 'Healthcare',
      challenge: 'Patient data siloed across 3 on-prem systems, making cross-system analysis impossible',
      solution: 'Unified data lake on AWS S3 with automated pipelines from all 3 systems, HIPAA-compliant architecture',
      result: '40% faster patient risk reporting. Full HIPAA compliance maintained throughout.',
      timeline: '10 weeks',
      link: '/case-studies/healthcare-ai'
    },
    {
      industry: 'Retail',
      challenge: 'Oracle ERP migration to cloud was stalled — custom schemas, 15 years of transactional data, no documentation',
      solution: 'Schema analysis, automated translation scripts, iterative migration with full data validation at every stage',
      result: '100% data integrity verified. Oracle licences cancelled saving $400K/year.',
      timeline: '12 weeks',
      link: '/case-studies/retail-analytics'
    }
  ];

  const process = [
    { step: '01', title: 'Discovery & Assessment', description: 'Catalogue every data source, assess volume, complexity, dependencies, and compliance requirements', duration: '1 week' },
    { step: '02', title: 'Migration Architecture', description: 'Design the target state, choose the right tools, and define the cut-over strategy with rollback plan', duration: '1 week' },
    { step: '03', title: 'Build, Test & Validate', description: 'Incremental migration with automated data validation at every step — no guessing if data is correct', duration: '4–10 weeks' },
    { step: '04', title: 'Cut-over & Decommission', description: 'Zero-downtime cut-over, post-migration monitoring, and decommission of legacy systems', duration: '1–2 weeks' }
  ];

  const techStack = [
    { category: 'Cloud Platforms', items: ['AWS', 'Microsoft Azure', 'Google Cloud', 'Snowflake', 'Databricks'] },
    { category: 'Migration Tools', items: ['AWS DMS', 'Azure Database Migration Service', 'Flyway', 'Liquibase', 'pgloader'] },
    { category: 'ETL / ELT', items: ['Apache Spark', 'AWS Glue', 'dbt', 'Fivetran', 'Airbyte'] },
    { category: 'CDC & Streaming', items: ['Debezium', 'Apache Kafka', 'AWS Kinesis', 'Azure Event Hubs'] },
    { category: 'Validation', items: ['Great Expectations', 'dbt tests', 'Custom reconciliation scripts', 'Monte Carlo'] },
    { category: 'Monitoring', items: ['CloudWatch', 'Datadog', 'Custom dashboards', 'Alerting pipelines'] }
  ];

  const guarantees = [
    { title: 'Zero Data Loss', description: 'Every migration includes automated reconciliation. We do not close a migration until every row is verified.' },
    { title: 'Minimal Downtime', description: 'CDC-based live migrations mean production keeps running. Most migrations achieve zero-downtime cut-over.' },
    { title: 'Fixed-Price Scope', description: 'We scope migrations with a fixed price and timeline. No surprise overruns after discovery.' },
    { title: 'Compliance Maintained', description: 'Your data stays compliant throughout — we handle HIPAA, GDPR, PCI-DSS data with appropriate controls.' }
  ];

  return (
    <>
      <Helmet>
        <title>Data Migration Services | Cloud Database & Warehouse Migration | Agility</title>
        <meta name="description" content="Expert data migration services: cloud migration, database migration, data warehouse migration, legacy system modernisation. AWS, Azure, GCP, Snowflake. Zero data loss guaranteed. Fixed-price delivery." />
        <meta name="keywords" content="data migration services, cloud data migration, database migration services, data warehouse migration, legacy data migration, AWS migration, Azure data migration, Snowflake migration, ETL migration, data modernisation services" />
        <link rel="canonical" href="https://www.agilitytech.ai/data-migration-services" />
        <meta property="og:title" content="Data Migration Services | Cloud, Database & Warehouse Migration | Agility" />
        <meta property="og:description" content="Zero-downtime data migrations to AWS, Azure, GCP, Snowflake. Database, warehouse, and legacy system migration. Fixed price, zero data loss." />
        <meta property="og:url" content="https://www.agilitytech.ai/data-migration-services" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Data Migration Services",
            "description": "Cloud data migration, database migration, data warehouse migration, and legacy system modernisation with zero data loss guarantee.",
            "provider": { "@type": "Organization", "name": "Agility", "url": "https://www.agilitytech.ai" },
            "areaServed": "Worldwide",
            "serviceType": "Data Migration"
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-black to-teal-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[linear-gradient(30deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6),linear-gradient(150deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6)]" style={{ backgroundSize: '80px 140px' }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/30 rounded-full mb-8">
            <Database className="w-4 h-4 text-teal-400" />
            <span className="text-teal-300 text-sm font-medium">Data Migration Services</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Data Migrations That <span className="text-teal-400">Actually Complete</span>
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl mx-auto">
            Cloud migration, database modernisation, and data warehouse moves — delivered with zero data loss,
            minimal downtime, and a fixed price. We've migrated petabytes across AWS, Azure, GCP, and Snowflake.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all">
              Scope My Migration <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/solutions/data-engineering" className="group inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold backdrop-blur-sm border border-white/20 transition-all">
              Data Engineering Services <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[['500TB+', 'Data Migrated'],['0', 'Data Loss Incidents'],['99%', 'On-Time Delivery'],['60%', 'Avg Cost Reduction']].map(([stat, label]) => (
              <div key={label} className="text-center">
                <div className="text-4xl font-bold text-teal-400 mb-2">{stat}</div>
                <div className="text-gray-400">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Every Type of <span className="text-teal-600">Data Migration</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, i) => {
              const Icon = service.icon;
              return (
                <div key={i} className="fade-in-up bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 border-2 border-gray-200 hover:border-teal-500 transition-all">
                  <Icon className="w-12 h-12 text-teal-600 mb-6" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {service.features.map((f, j) => (
                      <span key={j} className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm font-medium">{f}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Guarantees */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-teal-600">Migration Guarantee</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {guarantees.map((g, i) => (
              <div key={i} className="fade-in-up flex gap-6 bg-white rounded-xl p-8 border border-gray-200 hover:border-teal-400 transition-all">
                <CheckCircle2 className="w-10 h-10 text-teal-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{g.title}</h3>
                  <p className="text-gray-600">{g.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Migrations <span className="text-teal-600">We've Completed</span>
            </h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {caseStudies.map((study, i) => (
              <Link key={i} to={study.link} className="fade-in-up bg-gradient-to-br from-teal-50 to-white rounded-xl p-8 border border-teal-200 hover:border-teal-400 hover:shadow-lg transition-all group">
                <div className="text-sm font-semibold text-teal-600 mb-4">{study.industry}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Challenge</h3>
                <p className="text-gray-700 mb-4">{study.challenge}</p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Solution</h3>
                <p className="text-gray-700 mb-4">{study.solution}</p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Results</h3>
                <p className="text-gray-700 mb-6">{study.result}</p>
                <div className="flex items-center justify-between pt-6 border-t border-teal-200">
                  <div>
                    <div className="text-sm text-gray-500">Timeline</div>
                    <div className="font-semibold text-gray-900">{study.timeline}</div>
                  </div>
                  <div className="flex items-center gap-1 text-teal-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                    Read more <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              How We Run <span className="text-teal-400">Your Migration</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((p, i) => (
              <div key={i} className="fade-in-up text-center">
                <div className="w-20 h-20 bg-teal-500 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">{p.step}</div>
                <h3 className="text-xl font-bold text-white mb-3">{p.title}</h3>
                <p className="text-gray-300 mb-4">{p.description}</p>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-900/50 text-teal-300 rounded-full text-sm font-medium border border-teal-700">
                  <Clock className="w-4 h-4" /> {p.duration}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Migration <span className="text-teal-600">Tech Stack</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {techStack.map((t, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-bold text-teal-600 mb-4">{t.category}</h3>
                <div className="flex flex-wrap gap-2">
                  {t.items.map((item, j) => (
                    <span key={j} className="px-3 py-1 bg-white text-gray-700 rounded-full text-sm border border-gray-200">{item}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-teal-600 to-teal-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Move Your Data?</h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Share your current state and target platform. We'll scope your migration with a fixed price and timeline within 5 days.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 bg-white text-teal-700 hover:bg-teal-50 px-8 py-4 rounded-lg font-bold text-lg transition-all">
              Get a Migration Assessment <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default DataMigrationServices;
